import React from 'react';
import Header from '../component/Header';
import { motion } from 'framer-motion';
import Footer from '../component/Footer';
import aboutImg from '../assets/aboutthum.png';
import VisionandMission from '../component/VisionandMission';

import BoltIcon from '@mui/icons-material/Bolt';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DescriptionIcon from '@mui/icons-material/Description';
import CachedIcon from '@mui/icons-material/Cached';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SettingsIcon from '@mui/icons-material/Settings';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';

import Process from '../component/Process';
import Stats from '../component/Stats';

export default function About() {
    const container = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: 0.18,
                delayChildren: 0.2,
            },
        },
    };

    const fadeUp = {
        hidden: {
            opacity: 0,
            y: 30,
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: 'easeOut',
            },
        },
    };

    const cardHover = {
        y: -6,
        boxShadow: '0px 20px 40px rgba(0,255,255,0.08)',
        transition: { duration: 0.3 },
    };

    const list = [
        {
            icon: <BoltIcon />,
            title: 'Designed for Faster Support',
            desc: 'Digital verification workflows designed to make urgent financial support faster and easier to navigate.',
        },
        {
            icon: <DescriptionIcon />,
            title: 'Minimal Documentation',
            desc: 'A simpler online process with reduced paperwork requirements for eligible salaried professionals.',
        },
        {
            icon: <CachedIcon />,
            title: 'Transparent Process',
            desc: 'Clear communication around verification, repayment obligations, applicable charges, and the overall application journey.',
        },
        {
            icon: <SettingsIcon />,
            title: 'Responsible Borrowing Approach',
            desc: 'Built around structured lending practices that encourage informed financial decisions.',
        },
        {
            icon: <LockOutlinedIcon />,
            title: 'Secure Information Handling',
            desc: 'User information is managed through verification-focused and digitally structured processes.',
        },
        {
            icon: <CurrencyRupeeIcon />,
            title: 'Designed for Salary-Cycle Gaps',
            desc: 'Created for situations where expenses arrive before the next salary credit.',
        },
        {
            icon: <LocationOnIcon />,
            title: 'Expanding Across Cities',
            desc: 'Currently supporting users across 20+ cities with a growing digital lending ecosystem.',
        },
        {
            icon: <SupportAgentIcon />,
            title: 'Customer-First Support',
            desc: 'Focused on creating a smoother, clearer, and more user-friendly borrowing experience for working professionals.',
        },
    ];

    return (
        <>
            <Header />

            {/* Hero Section */}
            <div className="relative h-[300px] flex items-center justify-center bg-gradient-to-br from-[#0b1f3a] to-[#0a2540] overflow-hidden">
                <div className="absolute w-[400px] h-[400px] bg-blue-500/20 rounded-full blur-3xl animate-pulse top-[-100px] left-[-100px]" />
                <div className="absolute w-[300px] h-[300px] bg-orange-400/20 rounded-full blur-3xl animate-pulse bottom-[-80px] right-[-80px]" />

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="px-10 py-8 text-center"
                >
                    <motion.h1
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-4xl md:text-5xl font-bold text-white"
                    >
                        About <span className="text-orange-400">US</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="mt-4 text-gray-200 text-sm md:text-base"
                    >
                        B4Salary: Making Life Between Salary Cycles Easier
                    </motion.p>
                </motion.div>
            </div>

            {/* Intro Section */}
            <section className="bg-gray-100 py-20 px-4">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -80 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="relative"
                    >
                        <img
                            src={aboutImg}
                            alt="About"
                            className="rounded-2xl w-full h-auto object-cover"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 80 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <p className="ms-text-secondary uppercase font-semibold text-lg mb-2">
                            Our Story
                        </p>

                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-snug mb-4">
                            Built to Make Life Between Salary Cycles Easier
                        </h2>

                        <p className="text-gray-600 mb-4 leading-7">
                            B4Salary started with a simple observation — many
                            salaried professionals were not struggling because
                            they lacked income, but because expenses and salary
                            dates did not always align. In urgent situations,
                            people often turned to unstructured borrowing,
                            high-interest lenders, or informal loan sources
                            that created even more financial pressure over
                            time.
                        </p>

                        <p className="text-gray-600 mb-4 leading-7">
                            We believed there should be a simpler, more
                            transparent, and digitally structured way for
                            working professionals to explore short-term
                            financial support during salary-cycle gaps.
                        </p>

                        <p className="text-gray-600 mb-6 leading-7">
                            That is why B4Salary was built — to help eligible
                            users access a smoother online loan exploration
                            process through digital verification workflows and
                            our RBI-registered NBFC lending partner — Kisga
                            Leasing and Finance Pvt. Ltd.
                        </p>

                        <p className="text-gray-600 mb-6 leading-7">
                            We deliver scalable digital solutions that help
                            businesses grow, innovate, and succeed in a
                            competitive market.
                        </p>
                    </motion.div>
                </div>
            </section>

            <VisionandMission />

            {/* Features Section */}
            <section>
                <div className="max-w-7xl mx-auto py-20 px-4">
                    <p className="ms-text-secondary uppercase font-semibold text-lg mb-2">
                        Built Around Your Needs
                    </p>

                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-snug mb-4">
                        Why Salaried Professionals Choose B4Salary
                    </h2>

                    <motion.div
                        variants={container}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
                    >
                        {list.map((item, index) => (
                            <motion.div
                                key={index}
                                variants={fadeUp}
                                whileHover={cardHover}
                                className="p-6 border border-gray-300 rounded-lg mt-5 bg-white"
                            >
                                <span
                                    className="p-3 bg-orange-100 inline-flex"
                                    style={{ borderRadius: '50%' }}
                                >
                                    {item.icon}
                                </span>

                                <h3 className="text-xl font-semibold mb-3 pt-5">
                                    {item.title}
                                </h3>

                                <p className="text-gray-600 text-base pb-6">
                                    {item.desc}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <Process />
            <Stats />
            <Footer />
        </>
    );
}